import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Row, Button, Spinner, Alert, Input } from "reactstrap";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";
import { DirectoryFilters } from "./DirectoryFilters";
import { ResidentTable } from "./ResidentTable";
import { HeroBuilding } from "./HeroBuilding";
import { ImportRosterModal } from "./ImportRosterModal";
import { SendNoticeModal } from "./notices/SendNoticeModal";
import { canImportRoster, canSendNotices } from "./access";
import {
  getResidentDirectory,
  exportResidentDirectoryExcel,
  saveResidentContact,
  saveResidentUnit,
} from "../TenantForm/Helpers/api";
import "./residentDirectory.scss";

const PER_PAGE_OPTIONS = [25, 50, 100, 200];

// Current is the default view: current rows are editable in place, and those
// edits save to the Resident Directory's own store.
const STATUS_OPTIONS = [
  { value: "current", label: "Current" },
  { value: "past", label: "Past" },
  { value: "all", label: "All" },
];

/**
 * A vacant unit is part of the current picture (it comes from the live mastersheet), so it
 * shows under Current and All, never under Past.
 */
const matchesStatus = (row, status) => status === "all" || row.status === status;

const digitsOnly = (v) => String(v || "").replace(/\D+/g, "");

const SkeletonRows = () => (
  <div className="p-3">
    {Array.from({ length: 8 }).map((_, i) => (
      <div className="d-flex align-items-center gap-3 py-2" key={i}>
        <div className="rd-skeleton" style={{ width: 90 }} />
        <div className="rd-skeleton flex-grow-1" />
        <div className="rd-skeleton" style={{ width: 140 }} />
        <div className="rd-skeleton" style={{ width: 180 }} />
      </div>
    ))}
  </div>
);

/** Page numbers with ellipsis gaps, e.g. 1 … 4 5 6 … 20 */
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  let previous = 0;
  for (const p of sorted) {
    if (previous && p - previous > 1) out.push(`gap-${p}`);
    out.push(p);
    previous = p;
  }
  return out;
};

const ResidentDirectory = () => {
  const parsedAuthUser = JSON.parse(window.localStorage.getItem("authUser") || "{}");
  const userData = parsedAuthUser.userData || parsedAuthUser;
  // Only the company login imports from Excel; the server refuses everyone else, so the button
  // is not shown to them at all.
  const canImport = canImportRoster(userData);
  // Notices go out from the company login and from managers for their own properties.
  const canNotify = canSendNotices(userData);
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeTab, setNoticeTab] = useState("compose");

  const [filterData, setFilterData] = useState({
    property_name: "",
    property_id: "",
    building_name: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("current");
  const [perPage, setPerPage] = useState(100);
  const [page, setPage] = useState(1);
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Same rule as the Mastersheet: a building must be picked whenever the
  // property has any.
  const isValidSelection =
    !!filterData.property_id &&
    (filterData.building_list?.length === 0 || !!filterData.building_name);

  useEffect(() => {
    let active = true;
    if (!isValidSelection) {
      setData(null);
      setError(null);
      return undefined;
    }
    setLoading(true);
    setError(null);
    getResidentDirectory({
      property_id: filterData.property_id,
      building_name: filterData.building_name,
    })
      .then((resp) => {
        if (!active) return;
        if (!resp?.success) throw new Error(resp?.message || "Failed to load residents.");
        setData(resp.data);
        setPage(1);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err.message || "Failed to load residents.");
        setData(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isValidSelection, filterData.property_id, filterData.building_name, refreshKey]);

  const filtered = useMemo(() => {
    const rows = data?.rows || [];
    const term = search.trim().toLowerCase();
    const termDigits = digitsOnly(search);

    const matched = rows.filter((row) => {
      if (!matchesStatus(row, status)) return false;
      if (!term) return true;
      if (String(row.name).toLowerCase().includes(term)) return true;
      if (String(row.unitNo).toLowerCase().includes(term)) return true;
      if (String(row.email || "").toLowerCase().includes(term)) return true;
      if (termDigits.length >= 3 && digitsOnly(row.phone).includes(termDigits)) return true;
      return false;
    });

    // Unit order, numeric-aware so 2 sorts before 10.
    return [...matched].sort((a, b) => {
      const an = parseFloat(a.unitNo);
      const bn = parseFloat(b.unitNo);
      if (!isNaN(an) && !isNaN(bn) && an !== bn) return an - bn;
      const byUnit = String(a.unitNo).localeCompare(String(b.unitNo), undefined, { numeric: true });
      if (byUnit !== 0) return byUnit;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [data, search, status]);

  useEffect(() => setPage(1), [search, status, perPage]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, pageCount);
  const firstIndex = (currentPage - 1) * perPage;
  const pageRows = filtered.slice(firstIndex, firstIndex + perPage);
  // Rows are people plus one placeholder per vacant unit; the counts say which is which.
  const vacantCount = filtered.filter((r) => r.vacant).length;
  const peopleCount = filtered.length - vacantCount;
  const pagePeople = pageRows.filter((r) => !r.vacant).length;
  const countText = (shown, total) =>
    `${shown} of ${total} resident${total === 1 ? "" : "s"}` +
    (vacantCount ? ` · ${vacantCount} vacant unit${vacantCount === 1 ? "" : "s"}` : "");

  /**
   * Persist an inline contact edit to the Resident Directory store, then patch the
   * row in local state so the value survives re-filtering without a full refetch.
   * The cell reverts itself if this throws.
   */
  const handleSaveContact = async (row, patch) => {
    const res = await saveResidentContact({
      property_id: filterData.property_id,
      building_name: row.buildingName || filterData.building_name,
      unit_no: row.unitNo,
      name: row.name,
      // Tells the server which of the unit's lists (lease holders / occupants) the person is in.
      role: row.role,
      ...patch,
    });
    if (!res?.success) throw new Error(res?.message || "Save failed");

    const same = (r) =>
      r.unitNo === row.unitNo &&
      r.name === row.name &&
      (r.buildingName || "") === (row.buildingName || "") &&
      r.moveIn === row.moveIn;

    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) =>
              same(r) ? { ...r, ...patch, contactSource: null } : r
            ),
          }
        : prev
    );
    return res;
  };

  /**
   * Persist a unit-level edit (garage code, bins, or a resident rename) to the
   * Resident Directory store, then patch local rows so it survives re-filtering.
   */
  const handleSaveUnit = async (row, patch) => {
    const res = await saveResidentUnit({
      property_id: filterData.property_id,
      building_name: row.buildingName || filterData.building_name,
      unit_no: row.unitNo,
      ...patch,
    });
    if (!res?.success) throw new Error(res?.message || "Save failed");

    const sameUnit = (r) =>
      r.unitNo === row.unitNo && (r.buildingName || "") === (row.buildingName || "");
    setData((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((r) => {
              if (!sameUnit(r)) return r;
              const next = { ...r };
              if (r.status === "current") {
                if (patch.garage_code !== undefined) next.garageCode = patch.garage_code;
                if (patch.garbage_bin !== undefined) next.garbageBin = patch.garbage_bin;
                if (patch.composite_bin !== undefined) next.compositeBin = patch.composite_bin;
              }
              if (
                patch.name_edit !== undefined &&
                r.status === "current" &&
                r.name === patch.name_edit.from
              ) {
                next.name = patch.name_edit.to;
              }
              return next;
            }),
          }
        : prev
    );
    return res;
  };

  const handleImported = (result) => {
    Swal.fire(
      "Imported",
      `${result?.applied ?? 0} unit${(result?.applied ?? 0) === 1 ? "" : "s"} updated in the Resident Directory.`,
      "success"
    );
    setRefreshKey((k) => k + 1);
  };

  const handleExport = async () => {
    setDownloading(true);
    try {
      const blob = await exportResidentDirectoryExcel({
        property_id: filterData.property_id,
        building_name: filterData.building_name,
        property_name: filterData.property_name,
      });
      saveAs(new Blob([blob]), `Residents_${filterData.property_name || "property"}.xlsx`);
    } catch (err) {
      Swal.fire("Error", "Excel export failed.", "error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page-content resident-directory">
      <div className="container-fluid">
        <Row>
          <Col xl={12}>
            <Card className="overflow-hidden">
              <div className="rd-hero">
                <HeroBuilding />
                <div className="rd-hero-inner">
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <span className="rd-hero-icon" aria-hidden="true">
                      <i className="mdi mdi-account-group-outline" />
                    </span>
                    <div className="flex-grow-1">
                      <h1 className="rd-hero-title">Resident Directory</h1>
                      <p className="rd-hero-sub">
                        Everyone who has ever lived in this property — current and past — with
                        contact details filled in from signed leases.
                      </p>
                    </div>
                    {canNotify && (
                      <>
                        <button
                          type="button"
                          className="rd-hero-action rd-hero-action-primary d-flex align-items-center gap-2"
                          onClick={() => {
                            setNoticeTab("compose");
                            setNoticeOpen(true);
                          }}
                          disabled={!isValidSelection}
                          title={isValidSelection ? "Email a notice to the residents of this building" : "Pick a property and building first"}
                        >
                          <i className="mdi mdi-send-outline" aria-hidden="true" />
                          Send Notice
                        </button>
                        <button
                          type="button"
                          className="rd-hero-action d-flex align-items-center gap-2"
                          onClick={() => {
                            setNoticeTab("history");
                            setNoticeOpen(true);
                          }}
                          disabled={!isValidSelection}
                          title="Notices sent to this building"
                        >
                          <i className="mdi mdi-history" aria-hidden="true" />
                          Notices
                        </button>
                      </>
                    )}
                    {canImport && (
                      <button
                        type="button"
                        className="rd-hero-action d-flex align-items-center gap-2"
                        onClick={() => setImportOpen(true)}
                        disabled={!isValidSelection}
                      >
                        <i className="mdi mdi-tray-arrow-up" aria-hidden="true" />
                        Import Excel
                      </button>
                    )}
                    <button
                      type="button"
                      className="rd-hero-action d-flex align-items-center gap-2"
                      onClick={handleExport}
                      disabled={downloading || !data?.rows?.length}
                    >
                      {downloading ? (
                        <Spinner size="sm" />
                      ) : (
                        <i className="mdi mdi-tray-arrow-down" aria-hidden="true" />
                      )}
                      Download Excel
                    </button>
                  </div>

                  <DirectoryFilters
                    userData={userData}
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </div>
              </div>

              <CardBody>
                {!isValidSelection && (
                  <p className="text-muted text-center my-4 mb-0">
                    {filterData.property_id
                      ? "Select a building to see its residents."
                      : "Select a property to see its residents."}
                  </p>
                )}

                {error && <Alert color="danger">{error}</Alert>}

                {loading && <SkeletonRows />}

                {!loading && data && (
                  <>
                    <div className="rd-toolbar">
                      <div className="rd-search">
                        <label htmlFor="rd-search" className="visually-hidden">
                          Search residents
                        </label>
                        <i className="mdi mdi-magnify rd-search-icon" aria-hidden="true" />
                        <Input
                          id="rd-search"
                          type="search"
                          value={search}
                          placeholder="Search name, unit, email or phone..."
                          onChange={(e) => setSearch(e.target.value)}
                        />
                      </div>

                      <div
                        className="d-flex align-items-center gap-2 flex-wrap"
                        role="group"
                        aria-label="Resident status"
                      >
                        <span className="rd-chip-group-label">Show</span>
                        {STATUS_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className="rd-chip"
                            aria-pressed={status === option.value}
                            onClick={() => setStatus(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="rd-result-count" aria-live="polite">
                      Showing {countText(pagePeople, peopleCount)}
                      {status === "past"
                        ? " — past residents are historic records and cannot be edited"
                        : " — edits save to the Resident Directory as you make them"}
                    </div>

                    {filtered.length === 0 ? (
                      <div className="text-center my-4">
                        <p className="text-muted mb-2">
                          {search || status !== "current"
                            ? "No residents match these filters."
                            : "No current residents recorded for this property yet."}
                        </p>
                        {(search || status !== "current") && (
                          <Button
                            color="link"
                            onClick={() => {
                              setSearch("");
                              setStatus("current");
                            }}
                          >
                            Reset filters
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        <ResidentTable
                          rows={pageRows}
                          onSaveContact={handleSaveContact}
                          onSaveUnit={handleSaveUnit}
                        />

                        <div className="rd-footer">
                          <span>
                            Showing {firstIndex + 1} to {firstIndex + pageRows.length} of {filtered.length} rows
                          </span>

                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <label htmlFor="rd-perpage" className="visually-hidden">
                              Residents per page
                            </label>
                            <Input
                              id="rd-perpage"
                              type="select"
                              className="rd-perpage"
                              value={perPage}
                              onChange={(e) => setPerPage(Number(e.target.value))}
                            >
                              {PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                  {n} per page
                                </option>
                              ))}
                            </Input>

                            <nav aria-label="Resident pages" className="d-flex align-items-center gap-1">
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="First page"
                                disabled={currentPage === 1}
                                onClick={() => setPage(1)}
                              >
                                <i className="mdi mdi-chevron-double-left" />
                              </button>
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Previous page"
                                disabled={currentPage === 1}
                                onClick={() => setPage(currentPage - 1)}
                              >
                                <i className="mdi mdi-chevron-left" />
                              </button>

                              {pageWindow(currentPage, pageCount).map((entry) =>
                                typeof entry === "number" ? (
                                  <button
                                    key={entry}
                                    type="button"
                                    className="rd-page-btn"
                                    aria-label={`Page ${entry}`}
                                    aria-current={entry === currentPage ? "page" : undefined}
                                    onClick={() => setPage(entry)}
                                  >
                                    {entry}
                                  </button>
                                ) : (
                                  <span key={entry} className="rd-page-gap" aria-hidden="true">
                                    ...
                                  </span>
                                )
                              )}

                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Next page"
                                disabled={currentPage === pageCount}
                                onClick={() => setPage(currentPage + 1)}
                              >
                                <i className="mdi mdi-chevron-right" />
                              </button>
                              <button
                                type="button"
                                className="rd-page-btn"
                                aria-label="Last page"
                                disabled={currentPage === pageCount}
                                onClick={() => setPage(pageCount)}
                              >
                                <i className="mdi mdi-chevron-double-right" />
                              </button>
                            </nav>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      {canImport && (
        <ImportRosterModal
          isOpen={importOpen}
          onClose={() => setImportOpen(false)}
          filterData={filterData}
          onImported={handleImported}
        />
      )}

      {canNotify && noticeOpen && (
        <SendNoticeModal
          isOpen={noticeOpen}
          onClose={() => setNoticeOpen(false)}
          filterData={filterData}
          userData={userData}
          initialTab={noticeTab}
        />
      )}
    </div>
  );
};

export { ResidentDirectory };
